import flatpickr from "flatpickr";
import { French } from "flatpickr/dist/l10n/fr.js";

// Wrap everything in IIFE to allow return statement
(function () {
	// Prevent multiple initializations
	if (window.jsxPostsListingInitialized) {
		return;
	}
	window.jsxPostsListingInitialized = true;
	window.jsxPostsListingInitTime = Date.now();

	// Global variables for request management
	let latestRequestId = 0;
	let currentAbortController = null;
	let activeRequests = new Set();
	let dateRangeInstances = [];

	document.addEventListener("DOMContentLoaded", function () {
		// Cache frequently accessed DOM elements for better performance
		const cachedElements = {
			primaryChipsContainers: document.querySelectorAll(
				'[data-js="primary-selected-chips"]',
			),
			categorySelects: document.querySelectorAll(
				'select[data-js="category-filter"]',
			),
			popoverContainers: document.querySelectorAll(
				'[data-js="primary-popovers"]',
			),
			mobileFilterToggle: document.querySelector(
				'[data-js="mobile-filter-toggle"]',
			),
			mobileFilterPanel: document.querySelector(
				'[data-js="mobile-filter-panel"]',
			),
			mobileFilterClose: document.querySelectorAll(
				'[data-js="mobile-filter-close"]',
			),
			mobileFilterReset: document.querySelector(
				'[data-js="mobile-filter-reset"]',
			),
			mobileFilterApply: document.querySelector(
				'[data-js="mobile-filter-apply"]',
			),
		};

		// Primary filters popover interactions (support multiple containers: mobile + desktop)
		// Using event delegation to prevent duplicate listeners
		if (cachedElements.popoverContainers.length) {
			// Collect all popovers for close-all functionality
			const getAllPopovers = () => {
				const allPopovers = [];
				cachedElements.popoverContainers.forEach((popoverContainer) => {
					const popovers = popoverContainer.querySelectorAll(".filter-popover");
					popovers.forEach((p) => allPopovers.push(p));
				});
				return allPopovers;
			};

			// Function to position popover relative to trigger (desktop only)
			const positionPopover = (popover, trigger) => {
				// Only apply positioning on desktop (1150px and up)
				if (window.innerWidth < 1150) return;

				const panel = popover.querySelector('[data-js="filter-popover-panel"]');
				if (!panel) return;

				const triggerRect = trigger.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const viewportPadding = 20; // Padding from viewport edges
				const gapFromTrigger = 8; // Gap between trigger and panel

				// Constrain panel height to fit viewport with padding
				const maxPanelHeight = viewportHeight - viewportPadding * 2;
				panel.style.maxHeight = `${maxPanelHeight}px`;

				// Position horizontally (left-aligned with trigger)
				let left = triggerRect.left;
				const panelWidth = panel.offsetWidth || 250;

				// Adjust if overflowing right edge
				if (left + panelWidth > window.innerWidth - viewportPadding) {
					left = window.innerWidth - panelWidth - viewportPadding;
				}

				// Ensure not off left edge
				if (left < viewportPadding) {
					left = viewportPadding;
				}

				// Always position below trigger
				let top = triggerRect.bottom + gapFromTrigger;

				// Ensure panel doesn't overflow bottom of viewport
				const potentialBottom = top + panel.offsetHeight;
				if (potentialBottom > viewportHeight - viewportPadding) {
					top = Math.max(
						viewportPadding,
						viewportHeight - panel.offsetHeight - viewportPadding,
					);
				}

				// Apply positioning
				panel.style.left = `${left}px`;
				panel.style.top = `${top}px`;
			};

			const closeAllPopovers = (except) => {
				const allPopovers = getAllPopovers();
				allPopovers.forEach((p) => {
					if (except && p === except) return;
					p.classList.remove("is-open");
					const btn = p.querySelector('[data-js="filter-popover-trigger"]');
					if (btn) btn.setAttribute("aria-expanded", "false");
				});

				// Remove body scroll lock if no popovers are open (desktop only)
				if (!except && window.innerWidth >= 1150) {
					document.body.classList.remove("popover-open");
				}
			};

			// Event delegation for popover triggers - single listener for all triggers
			document.addEventListener("click", (e) => {
				const trigger = e.target.closest('[data-js="filter-popover-trigger"]');

				if (trigger) {
					e.preventDefault();
					const wrapper = trigger.closest(".filter-popover");
					if (!wrapper) return;

					const isOpening = !wrapper.classList.contains("is-open");
					closeAllPopovers(isOpening ? wrapper : null);

					if (isOpening) {
						wrapper.classList.add("is-open");
						trigger.setAttribute("aria-expanded", "true");
						// Apply positioning and body scroll lock on desktop only
						if (window.innerWidth >= 1150) {
							positionPopover(wrapper, trigger);
							document.body.classList.add("popover-open");
						}
					} else {
						wrapper.classList.remove("is-open");
						trigger.setAttribute("aria-expanded", "false");
						// Remove body scroll lock on desktop only
						if (window.innerWidth >= 1150) {
							document.body.classList.remove("popover-open");
						}
					}
					return;
				}

				// Close when clicking outside any popover
				const clickedInsideSome = Array.from(
					cachedElements.popoverContainers,
				).some((c) => c.contains(e.target));
				if (!clickedInsideSome) {
					closeAllPopovers();
				}
			});

			// Close on Escape
			document.addEventListener("keydown", (e) => {
				if (e.key === "Escape") closeAllPopovers();
			});

			// Reposition popovers on window resize (desktop only)
			window.addEventListener("resize", () => {
				if (window.innerWidth >= 1150) {
					const allPopovers = getAllPopovers();
					allPopovers.forEach((popover) => {
						if (popover.classList.contains("is-open")) {
							const trigger = popover.querySelector(
								'[data-js="filter-popover-trigger"]',
							);
							if (trigger) {
								positionPopover(popover, trigger);
							}
						}
					});
				}
			});
		}

		// Function to detect item count and apply appropriate classes
		function initializePopoverLayouts() {
			const popoverContents = document.querySelectorAll(
				".filter-popover__content",
			);

			popoverContents.forEach((content) => {
				// Count checkboxes (excluding select-all)
				const checkboxes = content.querySelectorAll(
					'input[type="checkbox"]:not([data-select-all])',
				);
				const itemCount = checkboxes.length;

				// Apply has-many-items class if more than 10 items
				if (itemCount > 10) {
					content.classList.add("has-many-items");
				} else {
					content.classList.remove("has-many-items");
				}
			});
		}

		// Function to adjust popover positioning to prevent overflow
		function adjustPopoverPositioning() {
			const popoverPanels = document.querySelectorAll(".filter-popover__panel");

			popoverPanels.forEach((panel) => {
				// Only adjust on desktop and if panel is visible
				if (window.innerWidth < 1024 || panel.style.display === "none") return;

				const trigger = panel
					.closest(".filter-popover")
					.querySelector(".filter-popover__trigger");
				if (!trigger) return;

				const triggerRect = trigger.getBoundingClientRect();
				const panelRect = panel.getBoundingClientRect();
				const viewportWidth = window.innerWidth;

				// Reset positioning classes
				panel.classList.remove("position-left");

				// Check if panel would overflow right edge
				if (triggerRect.left + panelRect.width > viewportWidth) {
					panel.classList.add("position-left");
				}
			});
		}

		// Initialize popover layouts on page load
		initializePopoverLayouts();

		// Adjust positioning when popovers open
		document.addEventListener("click", (e) => {
			if (e.target.closest('[data-js="filter-popover-trigger"]')) {
				// Small delay to allow popover to render
				setTimeout(() => {
					adjustPopoverPositioning();
				}, 10);
			}
		});

		// Adjust positioning on window resize
		window.addEventListener("resize", () => {
			adjustPopoverPositioning();
		});

		// Global change listener for all checkboxes - syncs mobile/desktop and updates chips
		document.addEventListener("change", (e) => {
			if (e.target && e.target.matches('input[type="checkbox"]')) {
				const withinGroup = e.target.closest('[data-js="category-checkboxes"]');
				if (withinGroup) {
					const taxonomy = withinGroup.getAttribute("data-taxonomy");

					// Handle Select All checkbox
					if (e.target.hasAttribute("data-select-all")) {
						setAllInTaxonomy(taxonomy, e.target.checked);
					} else {
						// First sync the changed checkbox across views, then update Select All
						syncCheckboxStates(e.target);
						updateSelectAllState(taxonomy);
					}

					// If the target was Select All, still sync that checkbox across views
					if (e.target.hasAttribute("data-select-all")) {
						syncCheckboxStates(e.target);
					}

					// Update chips for all taxonomies (primary and secondary)
					renderPrimaryChips("dom");

					// Update URL and posts
					const filters = getAllActiveFilters();
					updateURL(filters);
					// Reset load more state when filters change
					resetLoadMoreState();
					scheduleUpdate(filters);
				}
			}
		});

		// Lock mechanism to prevent multiple simultaneous calls
		let isUpdatingChips = false;

		// Function to sync checkbox states between mobile and desktop
		const syncCheckboxStates = (changedCheckbox) => {
			// Get taxonomy from the checkbox's parent group
			const parentGroup = changedCheckbox.closest(
				'[data-js="category-checkboxes"]',
			);
			if (!parentGroup) return;

			const taxonomy = parentGroup.getAttribute("data-taxonomy");
			const value = changedCheckbox.value;
			const isChecked = changedCheckbox.checked;

			// Find all checkboxes with the same taxonomy and value
			const allMatchingCheckboxes = document.querySelectorAll(
				`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input[type="checkbox"][value="${value}"]`,
			);

			// Update all matching checkboxes to match the changed one
			allMatchingCheckboxes.forEach((checkbox) => {
				if (checkbox !== changedCheckbox) {
					checkbox.checked = isChecked;
				}
			});
		};

		// Set all term checkboxes within a taxonomy (and sync Select All across views)
		const setAllInTaxonomy = (taxonomy, checked) => {
			const termCheckboxes = document.querySelectorAll(
				`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input.filter-checkbox:not([data-select-all])`,
			);
			termCheckboxes.forEach((cb) => {
				cb.checked = checked;
			});
			const selectAllBoxes = document.querySelectorAll(
				`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input.filter-checkbox[data-select-all="true"]`,
			);
			selectAllBoxes.forEach((cb) => {
				cb.checked = checked;
			});
		};

		// Update Select All checkbox state based on whether all terms are selected
		const updateSelectAllState = (taxonomy) => {
			const termCheckboxes = document.querySelectorAll(
				`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input.filter-checkbox:not([data-select-all])`,
			);
			const allChecked =
				termCheckboxes.length > 0 &&
				Array.from(termCheckboxes).every((cb) => cb.checked);
			const selectAllBoxes = document.querySelectorAll(
				`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input.filter-checkbox[data-select-all="true"]`,
			);
			selectAllBoxes.forEach((cb) => {
				cb.checked = allChecked;
			});
		};

		// Unified chip management function (all filters)
		const renderPrimaryChips = (dataSource = "dom", filters = null) => {
			// If already updating, skip this call
			if (isUpdatingChips) {
				return;
			}

			// Set lock
			isUpdatingChips = true;

			// Check if containers exist
			if (
				!cachedElements.primaryChipsContainers ||
				!cachedElements.primaryChipsContainers.length
			) {
				isUpdatingChips = false;
				return;
			}

			// Clear ALL containers first to prevent any duplication
			cachedElements.primaryChipsContainers.forEach((container) => {
				container.innerHTML = "";
			});

			const fragment = document.createDocumentFragment();
			const processedChips = new Set();

			if (dataSource === "dom") {
				// Get data from ALL DOM checkboxes (current state) across primary and secondary filters
				const allGroups = document.querySelectorAll(
					'[data-js="category-checkboxes"]',
				);

				allGroups.forEach((group) => {
					const taxonomy = group.getAttribute("data-taxonomy");
					if (!taxonomy) return;

					const checked = group.querySelectorAll(
						'input[type="checkbox"]:checked:not([data-select-all])',
					);

					checked.forEach((cb) => {
						const raw = (cb.parentElement?.textContent || cb.value).trim();
						const label = raw.replace(/\s*\(\d+\)$/, "");
						const chipKey = `${taxonomy}:${cb.value}`;

						// Skip if we've already processed this chip (avoid duplicates across views)
						if (processedChips.has(chipKey)) {
							return;
						}

						processedChips.add(chipKey);
						fragment.appendChild(createChip(taxonomy, cb.value, label));
					});
				});
			} else if (dataSource === "url" && filters) {
				// Get data from URL parameters (initial state) for all taxonomies
				filters.forEach((terms, taxonomy) => {
					terms.forEach((term) => {
						// Find the term name from the checkbox
						const checkbox = document.querySelector(
							`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input[type="checkbox"][value="${term}"]`,
						);

						if (checkbox) {
							const raw = (checkbox.parentElement?.textContent || term).trim();
							const label = raw.replace(/\s*\(\d+\)$/, "");
							fragment.appendChild(createChip(taxonomy, term, label));
						}
					});
				});
			}

			// Date range chip — read straight from the hidden inputs (always the
			// source of truth: pre-filled server-side on load, kept in sync by
			// applyDateRange/clearDateRangeFilters) rather than from `filters`,
			// so it works the same regardless of which dataSource branch ran above.
			const dateFromValue = document.querySelector(
				'[data-js="date-from-input"]',
			)?.value;
			const dateToValue = document.querySelector(
				'[data-js="date-to-input"]',
			)?.value;
			const dateDisplayValue = document.querySelector(
				'[data-js="date-range-input"]',
			)?.value;
			if (dateFromValue && dateToValue && dateDisplayValue) {
				fragment.appendChild(createDateChip(dateDisplayValue));
			}

			// Only update the first container
			const firstContainer = cachedElements.primaryChipsContainers[0];
			if (firstContainer) {
				firstContainer.appendChild(fragment);
			}

			// Release lock
			isUpdatingChips = false;
		};

		// Helper function to create chip elements
		const createChip = (taxonomy, value, label) => {
			const chip = document.createElement("button");
			chip.type = "button";
			chip.className = "chip chip--filter";
			chip.setAttribute("data-js", "chip-remove");
			chip.setAttribute("data-taxonomy", taxonomy);
			chip.setAttribute("data-term", value);
			chip.innerHTML = `<span class="chip__label">${label}</span><span class="chip__close" aria-hidden="true">×</span>`;
			return chip;
		};

		// Same chip markup as createChip, but marked data-taxonomy="dateRange"
		// so the shared chip-remove click handler can special-case it (clear the
		// date picker instead of unchecking a taxonomy checkbox).
		const createDateChip = (label) => {
			const chip = document.createElement("button");
			chip.type = "button";
			chip.className = "chip chip--filter";
			chip.setAttribute("data-js", "chip-remove");
			chip.setAttribute("data-taxonomy", "dateRange");
			chip.innerHTML = `<span class="chip__label">${label}</span><span class="chip__close" aria-hidden="true">×</span>`;
			return chip;
		};

		// Consolidated GSAP animation function for content transitions
		const animateContentTransition = (content, onComplete) => {
			if (!postsContainer) return;

			// Use GSAP for smooth transitions if available
			if (typeof gsap !== "undefined") {
				// Stop any existing shimmer animations
				gsap.killTweensOf(".skeleton-shimmer");

				// Fade out current content smoothly
				gsap.to(postsContainer, {
					duration: 0.2,
					opacity: 0,
					y: -10,
					ease: "power2.inOut",
					onComplete: () => {
						// Replace content immediately
						postsContainer.innerHTML = content;
						postsContainer.classList.remove("is-loading");
						postsContainer.removeAttribute("aria-busy");

						// Fade in new content
						setTimeout(() => {
							gsap.fromTo(
								postsContainer,
								{ opacity: 0, y: 10 },
								{
									duration: 0.3,
									opacity: 1,
									y: 0,
									ease: "power2.out",
									onComplete: onComplete,
								},
							);
						}, 50);
					},
				});
			} else {
				// Fallback to original behavior without CSS animations
				postsContainer.innerHTML = content;
				postsContainer.classList.remove("is-loading");
				postsContainer.removeAttribute("aria-busy");
				if (onComplete) onComplete();
			}
		};

		// Removed eager-load helper; host normalization proved sufficient

		// Some local dev setups return absolute image URLs with a different host
		// (e.g., *.local) that a physical device cannot resolve. Normalize ONLY
		// those local-dev-host mismatches to the current origin — genuinely
		// external images (e.g. remote event thumbnails from a partner API) must
		// never be rewritten to this site's origin, or they 404 after filtering
		// or load more.
		const isLocalDevHostMismatch = (hostname) => hostname.endsWith(".local");

		const normalizeCardImageHosts = () => {
			const container = document.querySelector(".jsx-posts-listing");
			if (!container) return;
			const currentOrigin = window.location.origin;
			const imgs = container.querySelectorAll(
				".post-card__media img:not(.post-card__content-type-icon)",
			);
			imgs.forEach((img) => {
				// Normalize src
				const src = img.getAttribute("src");
				if (src) {
					try {
						const url = new URL(src, currentOrigin);
						if (
							url.origin !== currentOrigin &&
							isLocalDevHostMismatch(url.hostname)
						) {
							const normalized = `${currentOrigin}${url.pathname}${url.search}${url.hash}`;
							img.setAttribute("src", normalized);
						}
					} catch (e) {}
				}

				// Normalize srcset
				const srcset = img.getAttribute("srcset");
				if (srcset) {
					const parts = srcset.split(",").map((s) => s.trim());
					const normalizedParts = parts.map((part) => {
						const [urlPart, descriptor] = part.split(/\s+(?=[^\s]+$)/);
						try {
							const u = new URL(urlPart, currentOrigin);
							if (
								u.origin === currentOrigin ||
								!isLocalDevHostMismatch(u.hostname)
							) {
								return part;
							}
							const n = `${currentOrigin}${u.pathname}${u.search}${u.hash}`;
							return descriptor ? `${n} ${descriptor}` : n;
						} catch (e) {
							return part; // leave as-is if parsing fails
						}
					});
					img.setAttribute("srcset", normalizedParts.join(", "));
				}
			});
		};

		// Inline <script> tags from AJAX-injected HTML do not execute.
		// Explicitly initialize GPT slots that appear after filtering/load more.
		const initializeGptSlots = (root = document) => {
			if (
				typeof window === "undefined" ||
				!window.googletag ||
				!window.googletag.cmd
			) {
				return;
			}

			const slotNodes = root.querySelectorAll('[id^="div-gpt-ad-"]');
			if (!slotNodes.length) return;

			window.googletag.cmd.push(() => {
				slotNodes.forEach((node) => {
					const slotId = node.id;
					if (!slotId || node.dataset.gptInitialized === "1") return;

					try {
						window.googletag.display(slotId);
						node.dataset.gptInitialized = "1";
					} catch (e) {}
				});
			});
		};

		const postsContainer = document.querySelector(".jsx-posts-listing");
		const paginationContainer = document.querySelector(".pagination"); // Container pour la pagination

		// Load more state variables
		let currentLoadedPage = 1;
		let isLoadingMore = false;
		let totalPosts = 0;

		// Function to reset load more state
		const resetLoadMoreState = () => {
			currentLoadedPage = 1;
			isLoadingMore = false;
			const loadMoreButton = getLoadMoreButton();
			if (loadMoreButton) {
				loadMoreButton.setAttribute("data-current-page", "1");
				loadMoreButton.disabled = false;
				loadMoreButton.textContent = "Charger plus";
				// Reset total posts count to be updated by the response
				totalPosts = 0;
			}
		};

		// Debounce to prevent multiple rapid updates that cause blinking
		let updateTimeoutId;
		let isUpdating = false;
		const scheduleUpdate = (filters, page = 1) => {
			if (updateTimeoutId) clearTimeout(updateTimeoutId);
			if (isUpdating) return; // Prevent overlapping updates

			updateTimeoutId = setTimeout(() => {
				isUpdating = true;
				updatePosts(filters, page).finally(() => {
					isUpdating = false;
				});
			}, 100);
		};

		// Elegant GSAP-powered skeleton functions
		const getSkeletonCount = () => {
			if (ajaxParams && ajaxParams.postCount && !ajaxParams.noLimit) {
				return parseInt(ajaxParams.postCount, 10) || 6;
			}
			return 6;
		};

		const buildSkeletonCard = () => {
			return (
				'<div class="skeleton-card">' +
				'<div class="skeleton skeleton-card__media"><div class="skeleton-shimmer"></div></div>' +
				'<div class="skeleton skeleton-card__title"><div class="skeleton-shimmer"></div></div>' +
				'<div class="skeleton skeleton-card__excerpt"><div class="skeleton-shimmer"></div></div>' +
				'<div class="skeleton skeleton-card__excerpt"><div class="skeleton-shimmer"></div></div>' +
				'<div class="skeleton-card__footer">' +
				'<div class="skeleton skeleton-chip"><div class="skeleton-shimmer"></div></div>' +
				'<div class="skeleton skeleton-date"><div class="skeleton-shimmer"></div></div>' +
				"</div>" +
				"</div>"
			);
		};

		const renderSkeletons = () => {
			if (!postsContainer) return;
			const count = getSkeletonCount();
			let html = "";
			for (let i = 0; i < count; i++) {
				html += buildSkeletonCard();
			}

			postsContainer.innerHTML = html;
			postsContainer.classList.add("is-loading");
			postsContainer.setAttribute("aria-busy", "true");

			// Add elegant GSAP animations
			if (typeof gsap !== "undefined") {
				const skeletonCards = postsContainer.querySelectorAll(".skeleton-card");
				gsap.fromTo(
					skeletonCards,
					{ opacity: 0, y: 20, scale: 0.95 },
					{
						duration: 0.4,
						opacity: 1,
						y: 0,
						scale: 1,
						ease: "power2.out",
						stagger: 0.1,
					},
				);

				// Create smooth shimmer animation
				const shimmerElements =
					postsContainer.querySelectorAll(".skeleton-shimmer");
				shimmerElements.forEach((shimmer) => {
					gsap.fromTo(
						shimmer,
						{ x: "-100%" },
						{
							x: "100%",
							duration: 1.5,
							ease: "none",
							repeat: -1,
							yoyo: false,
						},
					);
				});
			}
		};
		const currentUrl = window.location.origin;

		// Mobile Filter Panel functionality - using event delegation to prevent duplicate listeners
		// Unified click handler for mobile filter panel actions
		document.addEventListener("click", (e) => {
			// Open mobile filter panel
			if (e.target.closest('[data-js="mobile-filter-toggle"]')) {
				e.preventDefault();
				openMobilePanel();
				return;
			}

			// Close mobile filter panel
			if (e.target.closest('[data-js="mobile-filter-close"]')) {
				e.preventDefault();
				closeMobilePanel();
				return;
			}

			// Close panel when clicking overlay
			const mobilePanel = e.target.closest('[data-js="mobile-filter-panel"]');
			if (mobilePanel && e.target === mobilePanel) {
				closeMobilePanel();
				return;
			}
		});

		// GSAP Animation functions
		const openMobilePanel = () => {
			if (!cachedElements.mobileFilterPanel) return;

			// Show the panel first
			cachedElements.mobileFilterPanel.classList.add("is-open");
			document.body.style.overflow = "hidden";

			// Use GSAP if available, otherwise fallback to CSS
			if (typeof gsap !== "undefined") {
				const overlay = cachedElements.mobileFilterPanel.querySelector(
					".mobile-filter-panel__overlay",
				);
				const content = cachedElements.mobileFilterPanel.querySelector(
					".mobile-filter-panel__content",
				);

				// Set initial states
				gsap.set(overlay, { opacity: 0 });
				gsap.set(content, { x: "-100%" });

				// Animate in
				gsap
					.timeline()
					.to(overlay, { duration: 0.3, opacity: 1, ease: "power2.out" })
					.to(content, { duration: 0.4, x: "0%", ease: "power2.out" }, 0);
			}
		};

		const closeMobilePanel = () => {
			if (!cachedElements.mobileFilterPanel) return;

			// Use GSAP if available, otherwise fallback to CSS
			if (typeof gsap !== "undefined") {
				const overlay = cachedElements.mobileFilterPanel.querySelector(
					".mobile-filter-panel__overlay",
				);
				const content = cachedElements.mobileFilterPanel.querySelector(
					".mobile-filter-panel__content",
				);

				// Animate out
				gsap
					.timeline({
						onComplete: () => {
							// Hide the panel after animation
							cachedElements.mobileFilterPanel.classList.remove("is-open");
							document.body.style.overflow = "";
						},
					})
					.to(content, { duration: 0.3, x: "-100%", ease: "power2.in" })
					.to(overlay, { duration: 0.2, opacity: 0, ease: "power2.in" }, 0);
			} else {
				// CSS fallback
				cachedElements.mobileFilterPanel.classList.remove("is-open");
				document.body.style.overflow = "";
			}
		};

		// Reset and Apply filters - using event delegation to prevent duplicate listeners
		document.addEventListener("click", (e) => {
			// Reset filters
			if (e.target.closest('[data-js="mobile-filter-reset"]')) {
				e.preventDefault();
				// Uncheck all checkboxes across BOTH views (mobile + desktop)
				const allCheckboxes = document.querySelectorAll(
					'[data-js="category-checkboxes"] input[type="checkbox"]',
				);
				allCheckboxes.forEach((checkbox) => {
					checkbox.checked = false;
				});

				// After clearing, ensure each taxonomy's Select All reflects state
				const allGroups = document.querySelectorAll(
					'[data-js="category-checkboxes"]',
				);
				allGroups.forEach((group) => {
					const taxonomy = group.getAttribute("data-taxonomy");
					if (taxonomy) updateSelectAllState(taxonomy);
				});

				// Reset ALL dropdowns (not just inside the mobile panel)
				const categorySelects = document.querySelectorAll(
					'select[data-js="category-filter"]',
				);
				if (categorySelects.length) {
					categorySelects.forEach((select) => {
						select.selectedIndex = 0;
					});
				}

				// Clear the date range filter (mobile + desktop instances)
				clearDateRangeFilters();

				// Re-render primary chips to reflect cleared filters
				renderPrimaryChips("dom");

				// Apply the reset (URL + posts refresh)
				const filters = getAllActiveFilters();
				updateURL(filters);
				// Reset load more state when filters are reset
				resetLoadMoreState();
				scheduleUpdate(filters);
				return;
			}

			// Apply filters
			if (e.target.closest('[data-js="mobile-filter-apply"]')) {
				e.preventDefault();
				const filters = getAllActiveFilters();
				updateURL(filters);
				// Reset load more state when filters are applied
				resetLoadMoreState();
				scheduleUpdate(filters);
				closeMobilePanel();
				return;
			}
		});

		// Optimized function to collect all active filters
		const getAllActiveFilters = () => {
			const filters = new Map();

			// Find elements dynamically instead of relying on cached elements
			const categorySelects = document.querySelectorAll(
				'select[data-js="category-filter"]',
			);
			const categoryCheckboxGroups = document.querySelectorAll(
				'[data-js="category-checkboxes"]',
			);

			// Date range filter (mobile + desktop instances stay in sync, so
			// reading the first one found reflects the current state). Read
			// before the early return below since the date filter isn't a
			// taxonomy select/checkbox.
			const dateFrom = document.querySelector(
				'[data-js="date-from-input"]',
			)?.value;
			const dateTo = document.querySelector(
				'[data-js="date-to-input"]',
			)?.value;
			if (dateFrom && dateTo) {
				filters.set("dateFrom", [dateFrom]);
				filters.set("dateTo", [dateTo]);
			}

			// Early return if no elements found
			if (categorySelects.length === 0 && categoryCheckboxGroups.length === 0) {
				return filters;
			}

			// Process dropdowns efficiently
			if (categorySelects.length > 0) {
				categorySelects.forEach((select) => {
					const value = select.value;
					if (value && select.id) {
						filters.set(select.id, [value]);
					}
				});
			}

			// Process checkboxes efficiently
			if (categoryCheckboxGroups.length > 0) {
				categoryCheckboxGroups.forEach((checkboxGroup) => {
					const taxonomy = checkboxGroup.getAttribute("data-taxonomy");
					if (!taxonomy) return;

					// Use more efficient query for checked checkboxes
					const checkedBoxes = checkboxGroup.querySelectorAll(
						'input[type="checkbox"]:checked',
					);
					if (checkedBoxes.length > 0) {
						// Use Array.from with map for better performance
						const values = Array.from(checkedBoxes, (cb) => cb.value);
						filters.set(taxonomy, values);
					}
				});
			}

			return filters;
		};

		// Helper function to apply filters to DOM elements
		const applyFiltersToDOM = (filters) => {
			filters.forEach((terms, taxonomy) => {
				// Handle dropdowns
				const select = document.querySelector(
					`select[data-js="category-filter"][id="${taxonomy}"]`,
				);
				if (select && terms.length === 1) {
					select.value = terms[0];
				}

				// Handle checkboxes - sync BOTH mobile and desktop groups
				const checkboxGroups = document.querySelectorAll(
					`[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"]`,
				);
				checkboxGroups.forEach((checkboxGroup) => {
					const checkboxes = checkboxGroup.querySelectorAll(
						'input[type="checkbox"]',
					);
					checkboxes.forEach((checkbox) => {
						const isChecked = terms.includes(checkbox.value);
						checkbox.checked = isChecked;
					});
				});
			});
		};

		// Helper to get fresh button reference
		const getLoadMoreButton = () => {
			return document.querySelector('[data-js="load-more"]');
		};

		// Load more functionality
		const handleLoadMore = () => {
			const loadMoreButton = getLoadMoreButton();
			if (isLoadingMore || !loadMoreButton) {
				return;
			}

			isLoadingMore = true;

			// Generate unique request ID
			latestRequestId++;
			const myRequestId = latestRequestId;

			// Get current page from button or default to 1
			const currentPageFromButton = loadMoreButton
				? parseInt(loadMoreButton.getAttribute("data-current-page")) || 1
				: 1;
			currentLoadedPage = currentPageFromButton + 1;

			// Get data from button attributes, with fallback to ajaxParams
			totalPosts =
				parseInt(loadMoreButton.getAttribute("data-total-posts")) ||
				(ajaxParams && ajaxParams.totalPosts
					? parseInt(ajaxParams.totalPosts)
					: totalPosts);
			const initialCount =
				parseInt(loadMoreButton.getAttribute("data-initial-count")) ||
				(ajaxParams && ajaxParams.initialPostCount
					? parseInt(ajaxParams.initialPostCount)
					: 12);
			const loadMoreCount =
				parseInt(loadMoreButton.getAttribute("data-load-more-count")) ||
				(ajaxParams && ajaxParams.loadMoreCount
					? parseInt(ajaxParams.loadMoreCount)
					: 6);

			// Update button state
			loadMoreButton.disabled = true;
			loadMoreButton.textContent = "Chargement...";

			// Check if ajaxParams exists
			if (typeof ajaxParams === "undefined") {
				const currentButton = getLoadMoreButton();
				if (currentButton) {
					currentButton.disabled = false;
					currentButton.textContent = "Charger plus";
				}
				isLoadingMore = false;
				return;
			}

			const filters = getAllActiveFilters();
			const formData = new FormData();
			const requestId =
				Date.now() + "_" + Math.random().toString(36).substr(2, 9);

			formData.append("action", "filter_posts");
			formData.append("currentUrl", currentUrl);
			formData.append("nonce", ajaxParams.nonce);
			formData.append("postType", ajaxParams.postType);
			formData.append("postCount", ajaxParams.postCount);
			formData.append("noLimit", ajaxParams.noLimit);
			formData.append("isLoadMore", true);
			formData.append("loadMoreCount", loadMoreCount);
			formData.append("initialPostCount", initialCount);
			formData.append(
				"initialPostCountCms",
				(ajaxParams && ajaxParams.initialPostCountCms) || initialCount,
			);
			formData.append("paged", currentLoadedPage);
			formData.append("requestId", requestId);

			// Add filters
			filters.forEach((terms, taxonomy) => {
				formData.append(taxonomy, terms.join(","));
			});

			fetch(ajaxParams.ajaxUrl, {
				method: "POST",
				credentials: "same-origin",
				body: formData,
			})
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					return response.json();
				})
				.then((data) => {
					// Validate request ID - ignore stale responses
					if (myRequestId !== latestRequestId) {
						isLoadingMore = false;
						return;
					}

					// Strict response validation
					if (!data || typeof data !== "object") {
						throw new Error("Invalid response format");
					}

					if (data.success !== true) {
						throw new Error("Response indicated failure");
					}

					if (!data.data || typeof data.data !== "object") {
						throw new Error("Invalid response data");
					}

					const responseData = data.data;

					// Validate HTML is a string
					if (typeof responseData.html !== "string") {
						throw new Error("Response HTML is not a string");
					}

					if (!responseData.html.trim()) {
						// Empty response is OK, just means no more posts
						const currentButton = getLoadMoreButton();
						if (currentButton) {
							currentButton.disabled = true;
							currentButton.textContent = "Tous les articles sont affichés";
						}
						isLoadingMore = false;
						return;
					}

					let newPosts = []; // Declare newPosts outside the if block

					// Create a temporary container to parse the HTML
					const tempDiv = document.createElement("div");
					tempDiv.innerHTML = responseData.html;

					// Get the new posts from the response
					newPosts = tempDiv.querySelectorAll(".jsx-posts-listing__single");

					// Append each new post to the existing container
					newPosts.forEach((post, index) => {
						postsContainer.appendChild(post);
					});

					// Animate only the new posts
					if (typeof gsap !== "undefined" && newPosts.length > 0) {
						gsap.fromTo(
							newPosts,
							{ opacity: 0, y: 20, scale: 0.95 },
							{
								duration: 0.4,
								opacity: 1,
								y: 0,
								scale: 1,
								ease: "power2.out",
								stagger: 0.1,
							},
						);
					}

					// Normalize image hosts for new posts
					normalizeCardImageHosts();
					initializeGptSlots(postsContainer);

					// Re-init info bubbles if needed
					if (
						typeof window !== "undefined" &&
						typeof window.initInfoBubbles === "function"
					) {
						window.initInfoBubbles();
					}

					// Update button state based on whether more posts exist
					const actualPostsLoaded = postsContainer.children.length;

					// Calculate expected posts loaded based on pages
					const expectedPostsLoaded =
						initialCount + loadMoreCount * (currentLoadedPage - 1);

					// Check if we have more posts by comparing actual loaded vs total available
					// Also check if the last load returned fewer posts than expected (indicating we've reached the end)
					const hasMorePosts =
						actualPostsLoaded < totalPosts && newPosts.length > 0;

					// Get fresh button reference after DOM update
					const currentButton = getLoadMoreButton();
					if (currentButton) {
						if (hasMorePosts) {
							currentButton.disabled = false;
							currentButton.textContent = "Charger plus";
							currentButton.setAttribute(
								"data-current-page",
								currentLoadedPage,
							);
						} else {
							currentButton.disabled = true;
							currentButton.textContent = "Tous les articles sont affichés";
							currentButton.setAttribute(
								"data-current-page",
								currentLoadedPage,
							);
						}
					}
				})
				.catch((error) => {
					// Handle error
					console.error("AJAX Error:", error);
					console.error("Error details:", error.message);
					const currentButton = getLoadMoreButton();
					if (currentButton) {
						currentButton.disabled = false;
						currentButton.textContent = "Charger plus";
						currentLoadedPage = currentPageFromButton; // Revert page increment
						currentButton.setAttribute("data-current-page", currentLoadedPage);
					}
				})
				.finally(() => {
					isLoadingMore = false;
				});
		};

		// GSAP-powered post update function with smooth transitions
		const updatePosts = (filters, page = 1, isLoadMore = false) => {
			if (!postsContainer) return;

			// Check if ajaxParams exists
			if (typeof ajaxParams === "undefined") {
				return;
			}

			// Generate unique request signature for deduplication
			const requestSignature = JSON.stringify({
				filters: Array.from(filters.entries()),
				page: page,
				action: "filter",
			});

			// Skip if identical request is already in flight
			if (activeRequests.has(requestSignature)) {
				return Promise.resolve();
			}

			// Add to active requests
			activeRequests.add(requestSignature);

			// Generate unique request ID
			latestRequestId++;
			const myRequestId = latestRequestId;

			// Cancel previous request if exists
			if (currentAbortController) {
				currentAbortController.abort();
			}

			// Create new AbortController if available
			if (typeof AbortController !== "undefined") {
				currentAbortController = new AbortController();
			}

			// Reset load more state when filters change (not load more)
			if (!isLoadMore) {
				resetLoadMoreState();
			} else {
				// For load more, get the current page from the button
				const currentButton = getLoadMoreButton();
				const currentPageFromButton = currentButton
					? parseInt(currentButton.getAttribute("data-current-page")) || 1
					: 1;
				currentLoadedPage = currentPageFromButton;
			}

			// Use GSAP for smooth transitions if available
			if (typeof gsap !== "undefined") {
				// Stop any existing shimmer animations
				gsap.killTweensOf(".skeleton-shimmer");

				// Fade out current content smoothly
				gsap.to(postsContainer, {
					duration: 0.2,
					opacity: 0,
					y: -10,
					ease: "power2.inOut",
					onComplete: () => {
						// Show elegant skeleton loading state
						renderSkeletons();
					},
				});
			} else {
				// Fallback to original behavior
				renderSkeletons();
			}

			const formData = new FormData();
			formData.append("action", "filter_posts");
			formData.append("currentUrl", currentUrl);
			formData.append("nonce", ajaxParams.nonce);
			formData.append("postType", ajaxParams.postType);
			formData.append("postCount", ajaxParams.postCount);
			formData.append("noLimit", ajaxParams.noLimit);
			formData.append("isLoadMore", false); // Always false for filter updates
			formData.append("initialPostCount", ajaxParams.initialPostCount || 12);
			formData.append(
				"initialPostCountCms",
				ajaxParams.initialPostCountCms || ajaxParams.initialPostCount || 12,
			);
			formData.append("loadMoreCount", ajaxParams.loadMoreCount || 6);
			formData.append("paged", page); // Ajout du numéro de page

			// Ajouter les filtres
			filters.forEach((terms, taxonomy) => {
				formData.append(taxonomy, terms.join(","));
			});

			// Prepare fetch options
			const fetchOptions = {
				method: "POST",
				credentials: "same-origin",
				body: formData,
			};

			// Add abort signal if available
			if (currentAbortController) {
				fetchOptions.signal = currentAbortController.signal;
			}

			return fetch(ajaxParams.ajaxUrl, fetchOptions)
				.then((response) => response.json())
				.then((data) => {
					// Validate request ID - ignore stale responses
					if (myRequestId !== latestRequestId) {
						activeRequests.delete(requestSignature);
						return;
					}

					// Strict response validation
					if (!data || typeof data !== "object") {
						activeRequests.delete(requestSignature);
						throw new Error("Invalid response format");
					}

					if (data.success !== true) {
						activeRequests.delete(requestSignature);
						throw new Error("Response indicated failure");
					}

					if (!data.data || typeof data.data !== "object") {
						activeRequests.delete(requestSignature);
						throw new Error("Invalid response data");
					}

					const responseData = data.data;

					// Validate HTML is a string
					if (typeof responseData.html !== "string") {
						activeRequests.delete(requestSignature);
						throw new Error("Response HTML is not a string");
					}

					if (data.success) {
						// Use consolidated animation function
						animateContentTransition(responseData.html, () => {
							// After DOM changes, keep primary chips in sync
							renderPrimaryChips("dom");
							// Re-apply info bubbles on newly injected captions
							if (
								typeof window !== "undefined" &&
								typeof window.initInfoBubbles === "function"
							) {
								window.initInfoBubbles();
							}
							// Normalize image hosts for physical devices
							normalizeCardImageHosts();
							initializeGptSlots(postsContainer);
						});

						// Update pagination only if container exists and noLimit is false
						if (
							paginationContainer &&
							responseData.pagination &&
							!ajaxParams.noLimit
						) {
							paginationContainer.innerHTML = responseData.pagination;
						} else if (paginationContainer && ajaxParams.noLimit) {
							// Hide pagination if noLimit is true
							paginationContainer.innerHTML = "";
						}

						// Update or create load more button
						let currentButton = getLoadMoreButton();
						const shouldShowButton = responseData.has_more_posts !== false;

						if (shouldShowButton) {
							// Button should exist - create it if it doesn't, update it if it does
							if (!currentButton) {
								// Create the button if it doesn't exist
								const postsLayoutWrapper = document.querySelector(
									".posts-layout-wrapper",
								);
								if (postsLayoutWrapper) {
									const loadMoreWrapper = document.createElement("div");
									loadMoreWrapper.className = "load-more-wrapper";
									loadMoreWrapper.innerHTML = `
									<button 
										class="load-more-button" 
										data-js="load-more"
										data-current-page="${responseData.current_page || 1}"
										data-total-posts="${responseData.total_posts || 0}"
										data-initial-count="${ajaxParams.initialPostCount || 12}"
										data-load-more-count="${ajaxParams.loadMoreCount || 6}">
										Charger plus
									</button>
								`;
									postsLayoutWrapper.appendChild(loadMoreWrapper);
									currentButton = getLoadMoreButton();
								}
							}

							// Update button state
							if (currentButton) {
								// Update total posts count when filters change
								if (responseData.total_posts !== undefined) {
									totalPosts = responseData.total_posts;
									currentButton.setAttribute(
										"data-total-posts",
										responseData.total_posts,
									);
								}

								// Calculate if we should show the load more button
								const actualPostsLoaded = postsContainer.children.length;
								const shouldShowLoadMore = actualPostsLoaded < totalPosts;

								if (shouldShowLoadMore) {
									currentButton.disabled = false;
									currentButton.textContent = "Charger plus";
									currentButton.setAttribute(
										"data-current-page",
										responseData.current_page || 1,
									);
								} else {
									currentButton.disabled = true;
									currentButton.textContent = "Tous les articles sont affichés";
									currentButton.setAttribute(
										"data-current-page",
										responseData.current_page || 1,
									);
								}
							}
						} else {
							// Button should not exist - remove it if it does
							if (currentButton) {
								currentButton.closest(".load-more-wrapper")?.remove();
							}
						}
					} else {
						// Handle no results
						animateContentTransition("<p>Aucun résultat trouvé</p>");
					}
				})
				.catch((error) => {
					// Clean up active requests
					activeRequests.delete(requestSignature);

					// Handle abort separately (not an error)
					if (error.name === "AbortError") {
						return;
					}

					// Handle other errors
					animateContentTransition("<p>Une erreur est survenue</p>");
				})
				.finally(() => {
					// Always clean up request signature
					activeRequests.delete(requestSignature);
				});
		};

		// Fonction pour mettre à jour l'URL
		const updateURL = (filters, page = 1) => {
			const url = new URL(window.location.href);

			// Supprimer les paramètres existants
			Array.from(url.searchParams.keys()).forEach((key) => {
				url.searchParams.delete(key);
			});

			// Ajouter les nouveaux paramètres
			filters.forEach((terms, taxonomy) => {
				if (terms.length > 0) {
					url.searchParams.set(taxonomy, terms.join(","));
				}
			});

			// Ajouter la page actuelle
			if (page > 1) {
				url.searchParams.set("page", page);
			}

			window.history.pushState({}, "", url);
		};

		// Gestion des clics sur la pagination
		document.addEventListener("click", (e) => {
			if (e.target.closest(".pagination a")) {
				e.preventDefault();
				const link = e.target.closest(".pagination a");
				const url = new URL(link.href);
				const page =
					url.searchParams.get("page") || url.pathname.split("/page/")[1];
				const filters = getAllActiveFilters();
				updateURL(filters, page);
				scheduleUpdate(filters, page);
			}
		});

		// Category select dropdowns - using event delegation to prevent duplicate listeners
		document.addEventListener("change", (e) => {
			if (e.target.matches('select[data-js="category-filter"]')) {
				const filters = getAllActiveFilters();
				updateURL(filters);
				scheduleUpdate(filters);
			}
		});

		// Individual checkbox listeners removed - now handled globally

		// Mobile panel listeners removed - now handled globally

		// Add load more event listener only once
		document.addEventListener("click", (e) => {
			if (e.target.closest('[data-js="load-more"]')) {
				e.preventDefault();
				handleLoadMore();
			}
		});

		// Simple initialization function
		const initializeFiltersFromURL = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const initialFilters = new Map();

			urlParams.forEach((value, key) => {
				if (key !== "page") {
					const decodedValue = decodeURIComponent(value);
					initialFilters.set(key, decodedValue.split(","));
				}
			});

			if (initialFilters.size === 0) {
				return;
			}

			// Apply filters to DOM elements
			applyFiltersToDOM(initialFilters);

			// Render chips once
			renderPrimaryChips("url", initialFilters);
		};

		// Initialize filters from URL parameters
		const urlParams = new URLSearchParams(window.location.search);
		const hasUrlParams = Array.from(urlParams.keys()).some(
			(key) => key !== "page",
		);

		if (hasUrlParams) {
			setTimeout(initializeFiltersFromURL, 100);
		} else {
			setTimeout(() => renderPrimaryChips("dom"), 50);
		}

		// Chip remove handler - works on both mobile and desktop
		document.addEventListener("click", (e) => {
			const chip =
				e.target.closest && e.target.closest('[data-js="chip-remove"]');
			if (chip) {
				e.preventDefault();
				e.stopPropagation();
				const taxonomy = chip.getAttribute("data-taxonomy");
				const term = chip.getAttribute("data-term");

				if (taxonomy === "dateRange") {
					clearDateRangeFilters();
				} else {
					// Update checkboxes in BOTH mobile and desktop groups
					const selector = `[data-js="category-checkboxes"][data-taxonomy="${taxonomy}"] input[type="checkbox"][value="${term}"]`;
					const checkboxes = document.querySelectorAll(selector);
					checkboxes.forEach((cb) => {
						cb.checked = false;
					});

					// Ensure Select All is updated when a single chip is dismissed
					updateSelectAllState(taxonomy);
				}

				renderPrimaryChips("dom");
				const filters = getAllActiveFilters();
				updateURL(filters);
				// Reset load more state when chip is removed
				resetLoadMoreState();
				scheduleUpdate(filters);
			}
		});

		// --- Date range filter (flatpickr) ---

		const formatDateISO = (date) => {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		};

		// Builds a local Date from a Y-m-d string without going through
		// flatpickr's own parser — flatpickr parses defaultDate/setDate string
		// values using the *display* `dateFormat` ("d M Y"), so feeding it an
		// ISO "Y-m-d" string there silently fails to parse and falls back to
		// an unrelated garbage date (this is what caused the chip/calendar to
		// show a wrong date like "20 janv 2026" after reload or on the
		// not-yet-interacted-with mobile/desktop instance).
		const parseDateISO = (isoString) => {
			const [year, month, day] = isoString.split("-").map(Number);
			return new Date(year, month - 1, day);
		};

		// Writes the picked range into every mobile/desktop instance's hidden
		// inputs and syncs the other flatpickr instance(s) without re-firing
		// onClose (which would otherwise recurse), then applies the filter.
		const applyDateRange = (dateFrom, dateTo) => {
			document
				.querySelectorAll('[data-js="date-from-input"]')
				.forEach((input) => {
					input.value = dateFrom;
				});
			document
				.querySelectorAll('[data-js="date-to-input"]')
				.forEach((input) => {
					input.value = dateTo;
				});
			document
				.querySelectorAll('[data-js="date-range-clear"]')
				.forEach((button) => {
					button.hidden = !(dateFrom && dateTo);
				});

			dateRangeInstances.forEach((fp) => {
				const shouldBeDates = dateFrom && dateTo ? [dateFrom, dateTo] : [];
				const currentDates = fp.selectedDates.map(formatDateISO);
				const isInSync =
					currentDates.length === shouldBeDates.length &&
					currentDates.every((d, i) => d === shouldBeDates[i]);
				if (!isInSync) {
					fp.setDate(shouldBeDates.map(parseDateISO), false);
				}
			});

			renderPrimaryChips("dom");
			const filters = getAllActiveFilters();
			updateURL(filters);
			resetLoadMoreState();
			scheduleUpdate(filters);
		};

		const clearDateRangeFilters = () => {
			dateRangeInstances.forEach((fp) => fp.clear());
			document
				.querySelectorAll('[data-js="date-from-input"], [data-js="date-to-input"]')
				.forEach((input) => {
					input.value = "";
				});
			document
				.querySelectorAll('[data-js="date-range-clear"]')
				.forEach((button) => {
					button.hidden = true;
				});
		};

		document
			.querySelectorAll('[data-js="date-range-input"]')
			.forEach((input) => {
				const container = input.closest('[data-js="date-range-filter"]');
				const dateFromInput = container?.querySelector(
					'[data-js="date-from-input"]',
				);
				const dateToInput = container?.querySelector(
					'[data-js="date-to-input"]',
				);
				const initialDates =
					dateFromInput?.value && dateToInput?.value
						? [dateFromInput.value, dateToInput.value].map(parseDateISO)
						: [];

				const fp = flatpickr(input, {
					mode: "range",
					dateFormat: "d M Y",
					locale: French,
					defaultDate: initialDates,
					onClose: (selectedDates) => {
						if (selectedDates.length === 2) {
							applyDateRange(
								formatDateISO(selectedDates[0]),
								formatDateISO(selectedDates[1]),
							);
						}
					},
				});

				dateRangeInstances.push(fp);
			});

		document.addEventListener("click", (e) => {
			if (e.target.closest('[data-js="date-range-clear"]')) {
				e.preventDefault();
				clearDateRangeFilters();
				renderPrimaryChips("dom");
				const filters = getAllActiveFilters();
				updateURL(filters);
				resetLoadMoreState();
				scheduleUpdate(filters);
			}
		});
	});
})();
